import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import type { DocumentReference, Patient } from "fhir/r5";
import { oids } from "@/configs/Settings";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { usePractitionerRoleContext } from "@/contexts/PractitionerRoleContext";
import {
  createEmptyReleaseLine,
  useLocalPurchaseReleases,
  useWooCommerceProducts,
  type PurchaseReleaseLineInput,
  type WooProduct,
} from "src-next/modules/patient-supporting-documents";
import {
  isStoreApiReady,
  resolveStoreConfigFromOrganization,
} from "src-next/modules/patient-supporting-documents/marketplace/constants/organizationStoreRegistry";
import {
  collectApprovedDocIds,
  ecommercePatientReference,
  filterCategoriesForTransaction,
  findTransactionById,
  getBinaryUrl,
  getPatientCpf,
  getPatientDisplayName,
  isEcommerceOrganization,
  peekPatientTransactions,
  resolveDocsReleaseState,
  resolveTransactionOpState,
  usePatientSupportDocuments,
  useSubmitPurchaseRelease,
} from "src-next/modules/ecommerce";

type DialogStep = 0 | 1;

function defaultValidUntil(): string {
  return dayjs().add(90, "day").format("YYYY-MM-DD");
}

interface UseManageDocumentsReleaseParams {
  patient: Patient | null;
  organizationId?: string;
  transactionId?: string;
  onClose: () => void;
}

export function useManageDocumentsRelease({
  patient,
  organizationId,
  transactionId,
  onClose,
}: UseManageDocumentsReleaseParams) {
  const { organization } = useOrganizationContext();
  const { userPractitionerRole } = usePractitionerRoleContext();

  const storeConfig = useMemo(() => {
    if (!organization || !isEcommerceOrganization(organization)) return null;
    return resolveStoreConfigFromOrganization(organization);
  }, [organization]);

  const storeReady = Boolean(storeConfig && isStoreApiReady(storeConfig));
  const storeError =
    storeConfig?.integrationError ||
    (!storeConfig
      ? "Loja não vinculada a esta organização."
      : !storeReady
        ? "A loja ainda não está pronta para listar produtos."
        : null);

  const name = patient ? getPatientDisplayName(patient) : "";
  const cpf = patient ? getPatientCpf(patient) : "";
  const patientReference = patient?.id
    ? `Patient/${patient.id}`
    : cpf
      ? `Patient/${oids.cpf}-${cpf}`
      : "";

  const docs = usePatientSupportDocuments(
    patient,
    organizationId,
    Boolean(patient),
  );
  const releases = useLocalPurchaseReleases(patientReference || undefined, {
    organizationId,
    storeName: storeConfig?.name || organization?.name || "",
    enabled: Boolean(patientReference && organizationId),
  });
  const {
    products,
    loadProducts,
    loading: productsLoading,
    error: productsError,
  } = useWooCommerceProducts(storeConfig);

  const { submitRelease, submitting } = useSubmitPurchaseRelease();

  const [step, setStep] = useState<DialogStep>(0);
  const releaseReadyRef = useRef(false);
  const [lines, setLines] = useState<PurchaseReleaseLineInput[]>([]);
  const [validUntil, setValidUntil] = useState(defaultValidUntil);
  const [preview, setPreview] = useState<{ url: string; title: string } | null>(
    null,
  );
  const [previousOpen, setPreviousOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!patient) return;
    setStep(0);
    releaseReadyRef.current = false;
    setLines([]);
    setValidUntil(defaultValidUntil());
    setPreview(null);
    setPreviousOpen(false);
    setConfirmOpen(false);
    if (storeReady) void loadProducts();
  }, [patient, storeReady, loadProducts]);

  const scopedDocs = useMemo(
    () => filterCategoriesForTransaction(docs.categories, transactionId),
    [docs.categories, transactionId],
  );

  const { canRelease, allApproved: docsOk, hasRejected } = useMemo(
    () => resolveDocsReleaseState(scopedDocs.categories, organizationId),
    [scopedDocs.categories, organizationId],
  );

  const transactionOpState = useMemo(() => {
    if (!patient) return "unknown" as const;
    const key = ecommercePatientReference(patient);
    const transaction = findTransactionById(
      peekPatientTransactions(key),
      transactionId,
    );
    if (!transaction) return "unknown" as const;

    const live = { ...transaction };
    for (const category of scopedDocs.categories) {
      const doc = category.docs[0];
      if (!doc) continue;
      if (category.code === "medical_prescription") live.prescriptionDoc = doc;
      if (category.code === "anvisa_import_authorization") live.anvisaDoc = doc;
      if (category.code === "personal_document") {
        live.sharedDocs = { ...live.sharedDocs, personal: doc };
      }
      if (category.code === "proof_of_residence") {
        live.sharedDocs = { ...live.sharedDocs, residence: doc };
      }
    }

    return resolveTransactionOpState(live, key, organizationId);
  }, [patient, transactionId, organizationId, scopedDocs.categories]);

  useEffect(() => {
    if (!patient || docs.loading || docs.refreshing) return;
    const readyForRelease = docsOk && canRelease;
    if (readyForRelease && !releaseReadyRef.current) {
      setStep(1);
    }
    releaseReadyRef.current = readyForRelease;
  }, [patient, docs.loading, docs.refreshing, docsOk, canRelease]);

  useEffect(() => {
    if (step === 1 && !canRelease && !docs.loading && !docs.refreshing) {
      setStep(0);
    }
  }, [step, canRelease, docs.loading, docs.refreshing]);

  const openDoc = (doc: DocumentReference, title: string) => {
    const url = getBinaryUrl(doc);
    if (!url) {
      toast.info("Arquivo indisponível para visualização.");
      return;
    }
    setPreview({ url, title });
  };

  const handleAddProduct = (product: WooProduct) => {
    const todayIso = dayjs().format("YYYY-MM-DD");
    setLines((previous) =>
      previous.some((line) => line.product.id === product.id)
        ? previous
        : [...previous, createEmptyReleaseLine(product, todayIso, validUntil)],
    );
  };

  const handleUpdateQuantity = (productId: number, quantity: number) => {
    setLines((previous) =>
      quantity <= 0
        ? previous.filter((line) => line.product.id !== productId)
        : previous.map((line) =>
            line.product.id === productId ? { ...line, quantity } : line,
          ),
    );
  };

  const handleValidUntilChange = (value: string) => {
    setValidUntil(value);
    if (value) {
      setLines((previous) =>
        previous.map((line) => ({ ...line, validUntil: value })),
      );
    }
  };

  const handleConfirm = async () => {
    if (!storeConfig || !organizationId?.trim()) {
      toast.error("Loja ou organização não identificada.");
      return;
    }
    if (!lines.length) {
      toast.error("Inclua ao menos um produto.");
      return;
    }
    if (!validUntil) {
      toast.error("Informe até quando a liberação vale.");
      return;
    }

    const releasedAt = dayjs().format("YYYY-MM-DD");
    if (dayjs(validUntil).isBefore(dayjs(releasedAt), "day")) {
      toast.error(
        "“Paciente pode comprar até” deve ser hoje ou uma data futura.",
      );
      return;
    }

    for (const line of lines) {
      if (line.quantity < 1) {
        toast.error(`Quantidade inválida para ${line.product.name}.`);
        return;
      }
    }

    try {
      const batch = await submitRelease({
        patientReference,
        organizationId: organizationId.trim(),
        organizationFhirId: storeConfig.organizationId,
        practitionerRoleId: userPractitionerRole?.id,
        storeId: storeConfig.id,
        storeName: storeConfig.name,
        supportingDocumentIds: collectApprovedDocIds(
          scopedDocs.categories,
          organizationId,
        ),
        transactionId:
          transactionId && !transactionId.startsWith("pending:")
            ? transactionId
            : undefined,
        lines: lines.map((line) => ({ ...line, releasedAt, validUntil })),
      });
      releases.addBatch(batch);
      toast.success(
        lines.length === 1
          ? "Produto liberado para compra."
          : `${lines.length} produtos liberados para compra.`,
      );
      setConfirmOpen(false);
      onClose();
    } catch (error) {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : "Não foi possível gravar a liberação.",
      );
    }
  };

  const summaryText = lines.length
    ? `${lines.length} ${lines.length === 1 ? "produto selecionado" : "produtos selecionados"}`
    : "Nenhum produto selecionado";

  const canConfirm =
    canRelease &&
    storeReady &&
    lines.length > 0 &&
    !submitting &&
    !docs.refreshing &&
    !releases.refreshing;

  return {
    name,
    practitionerRoleId: userPractitionerRole?.id,
    docs: {
      ...docs,
      categories: scopedDocs.categories,
    },
    transactionId,
    transactionOpState,
    inheritedDocIds: scopedDocs.inheritedDocIds,
    releases,
    products,
    productsLoading,
    productsError,
    storeReady,
    storeError: storeError ?? null,
    submitting,
    step,
    setStep,
    lines,
    validUntil,
    preview,
    setPreview,
    previousOpen,
    setPreviousOpen,
    confirmOpen,
    setConfirmOpen,
    canRelease,
    docsOk,
    hasRejected,
    openDoc,
    handleAddProduct,
    handleUpdateQuantity,
    handleValidUntilChange,
    handleConfirm,
    summaryText,
    canConfirm,
    docsInteractionLocked: docs.refreshing || submitting,
    today: dayjs().startOf("day"),
  };
}
